import copy from '../../copy/en/shell.json' with { type: 'json' };
import { ChevronDown } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { Link, useLocation } from "react-router";
import type { PortalCapability } from "../../shared/roles.js";
import { cx } from "../components/ui/cx.js";
import { Brand } from "./Brand.js";
import { NAV_GROUPS, NAV_ITEMS, type NavItem } from "./navManifest.js";

export interface NavVisibilityInput {
  capabilities: readonly PortalCapability[];
}

/**
 * Which nav items a user sees. This is presentation only; the route and server
 * gates independently enforce the same capabilities.
 */
export function visibleNavItems({
  capabilities,
}: NavVisibilityInput): NavItem[] {
  return NAV_ITEMS.filter(
    (item) => !item.capability || capabilities.includes(item.capability),
  );
}

function NavItems({
  items,
  onNavigate,
}: {
  items: NavItem[];
  onNavigate?: () => void;
}) {
  const { pathname } = useLocation();
  return (
    <ul className="sidenav__list">
      {items.map((item) => {
        const active = pathname === item.to || (item.to !== "/" && pathname.startsWith(`${item.to}/`))
          || (item.id === "documents" && pathname === "/document-updates");
        return (
        <li key={item.id}>
          <Link
            to={item.to}
            aria-current={active ? "page" : undefined}
            onClick={onNavigate}
            className={
              cx(
                "sidenav__link",
                active && "is-active",
                item.upcomingPhase !== undefined && "is-upcoming",
              )
            }
          >
            <item.icon className="sidenav__icon" aria-hidden />
            <span className="sidenav__label">{item.label}</span>
          </Link>
        </li>
      ); })}
    </ul>
  );
}

function NavGroup({ label, collapsible, items, onNavigate }: {
  label: string; collapsible: boolean; items: NavItem[]; onNavigate?: () => void;
}) {
  const { pathname } = useLocation();
  const active = items.some(item => pathname === item.to || (item.to !== "/" && pathname.startsWith(`${item.to}/`)));
  const [open, setOpen] = useState(!collapsible || active);
  const id = useId();
  useEffect(() => { if (active) setOpen(true); }, [active, pathname]);
  return <div className="sidenav__group" role="group" aria-label={label}>
    {collapsible ? <button type="button" className="sidenav__disclosure" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
      <span>{label}</span><ChevronDown width={15} height={15} aria-hidden />
    </button> : <div className="sidenav__group-label" aria-hidden="true">{label}</div>}
    <div id={id} className="sidenav__collapse" data-open={open} inert={!open || undefined} aria-hidden={!open || undefined}><div><NavItems items={items} onNavigate={onNavigate} /></div></div>
  </div>;
}

export function SideNav({
  items,
  onNavigate,
  productSub = copy.SideNav.staff,
}: {
  items: NavItem[];
  onNavigate?: () => void;
  productSub?: string;
}) {
  const { pathname } = useLocation();
  const planned = items.filter((item) => item.upcomingPhase !== undefined);
  const isPlannedRoute = planned.some(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
  const [plannedOpen, setPlannedOpen] = useState(isPlannedRoute);
  const plannedId = useId();
  useEffect(() => {
    if (isPlannedRoute) setPlannedOpen(true);
  }, [isPlannedRoute, pathname]);
  return (
    <>
      <div className="sidenav__brand">
        <Brand sub={productSub} />
      </div>
      <nav aria-label={copy.SideNav.primary}>
        {NAV_GROUPS.map(group => {
          const groupItems = items.filter(item => item.upcomingPhase === undefined && (group.ids as readonly string[]).includes(item.id));
          return groupItems.length > 0 ? (
            <NavGroup key={group.label} label={group.label} collapsible={group.collapsible} items={groupItems} onNavigate={onNavigate} />
          ) : null;
        })}
        <NavItems items={items.filter(item => item.upcomingPhase === undefined && !NAV_GROUPS.some(group => (group.ids as readonly string[]).includes(item.id)))} onNavigate={onNavigate} />
      </nav>
      {planned.length > 0 ? (
        <div className="sidenav__planned">
          <button
            type="button"
            className="sidenav__disclosure"
            aria-expanded={plannedOpen}
            aria-controls={plannedId}
            onClick={() => setPlannedOpen(!plannedOpen)}
          >
            <span>{copy.SideNav.planned}</span>
            <ChevronDown width={15} height={15} aria-hidden />
          </button>
          <div id={plannedId} className="sidenav__collapse" data-open={plannedOpen} inert={!plannedOpen || undefined} aria-hidden={!plannedOpen || undefined}><div>
            <p className="sidenav__planned-hint">{copy.SideNav.notAvailableYet}</p>
            <nav aria-label={copy.SideNav.planned}>
              <NavItems items={planned} onNavigate={onNavigate} />
            </nav>
          </div></div>
        </div>
      ) : null}
    </>
  );
}
